import {
  REQUEST_CONTEXT_COOKIE_NAME,
  REQUEST_CONTEXT_UNKNOWN,
  readRequestContextCookie,
} from '@highereducation/request-context';

/**
 * Linking Identify for one page load.
 *
 * The Cohesion SDK is not held. One `core.Identify.v1` goes out when
 * `tagular:ready` and a known partner trip id have both arrived, in
 * either order. Unknown stays unknown: a missing id does not send, and
 * no id is invented. The beam call leaves the Cohesion session id alone;
 * the SDK attaches the anonymous id and session id.
 *
 * The partner trip id is `externalIds[].id`. `type` is `partner_trip_id`.
 * A partner session id is not an identity field and is not sent here.
 * A blank type does not send. `collection` and `encoding` are the fields
 * this event already accepts. `traits` is required and stays empty; this
 * link has none.
 */
export const PARTNER_VISIT_IDENTIFY_EVENT = 'core.Identify.v1';

/** Data-team name for the partner trip id on `externalIds[].type`. */
export const PARTNER_VISIT_EXTERNAL_ID_TYPE = 'partner_trip_id';

/** How often to look for `window.cohesion` and retry a beam that did not run. */
export const PARTNER_VISIT_IDENTIFY_RETRY_MS = 50;

const MAX_READY_POLLS = 600;

export interface PartnerVisitExternalId {
  readonly collection: 'users';
  readonly encoding: 'none';
  readonly id: string;
  readonly type: string;
}

export interface PartnerVisitIdentifyPayload {
  readonly '@type': typeof PARTNER_VISIT_IDENTIFY_EVENT;
  readonly externalIds: readonly [PartnerVisitExternalId];
  /** `core.Identify.v1` requires traits. This link sends none. */
  readonly traits: Record<string, never>;
}

/**
 * Returns true only after `window.tagular` has been called.
 * A missing `tagular` returns false and does not count as a send.
 */
export type PartnerVisitBeam = (
  eventName: typeof PARTNER_VISIT_IDENTIFY_EVENT,
  payload: PartnerVisitIdentifyPayload,
) => boolean;

export type PartnerVisitSendResult = 'sent' | 'not-invoked' | 'skipped';

type CohesionReady = (event: 'tagular:ready', callback: () => void) => void;

let sentForThisPage = false;

/** Test-only. A page load sends at most one Identify. */
export function resetPartnerVisitIdentifyForTests(): void {
  sentForThisPage = false;
}

export function knownPartnerTripId(
  value: string | undefined,
): string | undefined {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0 || trimmed === REQUEST_CONTEXT_UNKNOWN) {
    return undefined;
  }
  return trimmed;
}

function readCookieValue(
  cookieHeader: string,
  name: string,
): string | undefined {
  const prefix = `${name}=`;
  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return undefined;
}

/**
 * Partner trip id from `rv_ctx`. A missing or invalid cookie is unknown.
 * This does not map partner headers.
 */
export function readPartnerTripId(cookieHeader?: string): string {
  const source =
    cookieHeader ?? (typeof document === 'undefined' ? '' : document.cookie);
  const raw = readCookieValue(source, REQUEST_CONTEXT_COOKIE_NAME);
  let decoded = raw;
  if (raw) {
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      decoded = raw;
    }
  }
  return readRequestContextCookie(decoded).partnerTripId;
}

export function createPartnerVisitIdentifyLink(options: {
  readonly externalIdType?: string;
  readonly beam: PartnerVisitBeam;
}): {
  onTagularReady: () => PartnerVisitSendResult;
  retrySend: () => PartnerVisitSendResult;
  onVisitId: (value: string | undefined) => PartnerVisitSendResult;
} {
  let ready = false;
  let visitId: string | undefined;

  const trySend = (): PartnerVisitSendResult => {
    if (sentForThisPage) return 'sent';
    if (!ready || !visitId) return 'skipped';
    const type = options.externalIdType?.trim() ?? '';
    if (type.length === 0) return 'skipped';
    const invoked = options.beam(PARTNER_VISIT_IDENTIFY_EVENT, {
      '@type': PARTNER_VISIT_IDENTIFY_EVENT,
      externalIds: [
        {
          collection: 'users',
          encoding: 'none',
          id: visitId,
          type,
        },
      ],
      traits: {},
    });
    if (invoked !== true) return 'not-invoked';
    sentForThisPage = true;
    return 'sent';
  };

  return {
    onTagularReady(): PartnerVisitSendResult {
      ready = true;
      return trySend();
    },
    /** Another attempt after tagular was missing. Does nothing until ready. */
    retrySend(): PartnerVisitSendResult {
      return trySend();
    },
    onVisitId(value): PartnerVisitSendResult {
      const known = knownPartnerTripId(value);
      if (known) visitId = known;
      return trySend();
    },
  };
}

/**
 * Subscribes to `tagular:ready` once `window.cohesion` exists.
 * An already-ready Tagular is handled on that same turn, because a late
 * `tagular:ready` listener does not always run. The page-sent flag stays
 * clear until `beam` reports that tagular was called.
 */
export function watchPartnerVisitIdentify(options: {
  readonly externalIdType?: string;
  readonly beam: PartnerVisitBeam;
  readonly readVisitId: () => string;
  readonly getCohesion: () => CohesionReady | undefined;
  readonly isTagularReady: () => boolean;
  readonly schedule: (callback: () => void, delayMs: number) => () => void;
}): () => void {
  let stopped = false;
  let subscribed = false;
  let polls = 0;
  let stopRetry: (() => void) | undefined;
  const link = createPartnerVisitIdentifyLink({
    externalIdType: options.externalIdType,
    beam: options.beam,
  });

  const send = (): void => {
    if (stopped) return;
    link.onVisitId(options.readVisitId());
    if (link.onTagularReady() !== 'not-invoked' || stopRetry) return;
    let retries = 0;
    stopRetry = options.schedule(() => {
      retries += 1;
      if (
        stopped ||
        retries > MAX_READY_POLLS ||
        link.retrySend() !== 'not-invoked'
      ) {
        stopRetry?.();
        stopRetry = undefined;
      }
    }, PARTNER_VISIT_IDENTIFY_RETRY_MS);
  };

  const subscribe = (): void => {
    if (stopped || subscribed) return;
    const cohesion = options.getCohesion();
    if (typeof cohesion !== 'function') return;
    subscribed = true;
    cohesion('tagular:ready', send);
    if (options.isTagularReady()) send();
  };

  subscribe();
  const stopPoll = options.schedule(() => {
    polls += 1;
    subscribe();
    if (subscribed || polls >= MAX_READY_POLLS || stopped) stopPoll();
  }, PARTNER_VISIT_IDENTIFY_RETRY_MS);

  return () => {
    stopped = true;
    stopPoll();
    stopRetry?.();
  };
}
